import React from 'react';
import { Loader2, Play, Share2, Trash2 } from 'lucide-react';

/**
 * Inline list of saved demo-toolkit presets (recording + script + audio).
 * Renders inside the "Actions" tab of the Demo Toolkit when the user
 * expands the presets accordion. Pure UI — handlers and data come from
 * the parent.
 */
export default function DemoPresetsList({
  presets,
  presetsLoading,
  busy,
  playPreset,
  copyPublicLink,
  deletePreset,
}) {
  return (
    <div
      className="max-h-64 overflow-y-auto rounded-md border border-fuchsia-100 bg-fuchsia-50/50 p-1.5"
      data-testid="toolkit-presets-list"
    >
      {presetsLoading && (
        <div className="flex items-center gap-2 p-2 text-[11px] text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      )}
      {!presetsLoading && presets.length === 0 && (
        <div className="p-2 text-[11px] text-slate-500 italic">
          No presets yet. Record → generate script → play aloud → save.
        </div>
      )}
      {!presetsLoading &&
        presets.map((clip) => {
          const secs = Math.round((clip.total_ms || 0) / 1000);
          const mm = Math.floor(secs / 60);
          const ss = String(secs % 60).padStart(2, '0');
          return (
            <div
              key={clip.clip_id}
              className="flex items-start gap-1.5 rounded-md bg-white border border-fuchsia-100 px-2 py-1.5 mb-1 last:mb-0"
              data-testid={`preset-row-${clip.clip_id}`}
            >
              <div className="flex-1 min-w-0">
                <div
                  className="text-[11px] font-semibold text-slate-800 truncate"
                  title={clip.scenario}
                >
                  {clip.scenario}
                </div>
                <div className="flex items-center gap-1 mt-0.5 text-[10px] text-slate-500">
                  <span className="uppercase tracking-wider">{clip.voice}</span>
                  <span>·</span>
                  <span>
                    {mm}:{ss}
                  </span>
                  {clip.tags?.length > 0 && (
                    <>
                      <span>·</span>
                      <span className="truncate">{clip.tags.slice(0, 3).join(', ')}</span>
                    </>
                  )}
                </div>
                {clip.description && (
                  <div
                    className="text-[10px] text-slate-500 truncate mt-0.5"
                    title={clip.description}
                  >
                    {clip.description}
                  </div>
                )}
                {clip.analytics && clip.analytics.views > 0 && (
                  <div
                    className="mt-1 flex items-center gap-2 text-[10px] font-medium"
                    data-testid={`preset-analytics-${clip.clip_id}`}
                  >
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-700">
                      {clip.analytics.views} view{clip.analytics.views === 1 ? '' : 's'}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                      {clip.analytics.avg_completion_pct}% avg
                    </span>
                    {clip.analytics.cta_clicks > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">
                        {clip.analytics.cta_clicks} CTA
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => playPreset(clip)}
                disabled={busy === `play-${clip.clip_id}`}
                className="rounded-md p-1.5 text-fuchsia-700 transition-colors hover:bg-fuchsia-50 disabled:opacity-60"
                title="Play this preset"
                data-testid={`preset-play-${clip.clip_id}`}
              >
                {busy === `play-${clip.clip_id}` ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                onClick={() => copyPublicLink(clip)}
                disabled={busy === `share-${clip.clip_id}`}
                className="rounded-md p-1.5 text-indigo-600 transition-colors hover:bg-indigo-50 disabled:opacity-60"
                title="Copy a 7-day public preview link (no login required for prospects)"
                data-testid={`preset-share-${clip.clip_id}`}
              >
                {busy === `share-${clip.clip_id}` ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Share2 className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                onClick={() => deletePreset(clip)}
                disabled={busy === `del-${clip.clip_id}`}
                className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:opacity-60"
                title="Delete preset"
                data-testid={`preset-delete-${clip.clip_id}`}
              >
                {busy === `del-${clip.clip_id}` ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          );
        })}
    </div>
  );
}
